import { Bell } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { getNotificationPermission, requestNotificationPermission, showNotification } from '../../lib/notifications/desktopNotifications';
import { useNotificationsQuery } from '../../hooks/useNotifications';
import { useNotificationCenter } from '../../context/NotificationContext';

/**
 * Notification Bell Component
 * 
 * Displays a bell icon with unread count badge and opens notification center on click
 * Also handles desktop notifications when new notifications arrive
 */
export function NotificationBell() {
  const { openNotificationCenter } = useNotificationCenter();
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'default'>('default');
  const { data: notifications = [] } = useNotificationsQuery();
  
  // Calculate unread count from notifications array to match modal display
  // This ensures the badge count matches what's shown in the notification center
  // Handle both false and null/undefined values for is_read
  const unreadCount = notifications.filter((n) => {
    return n.is_read === false || n.is_read == null;
  }).length;

  // Check notification permission on mount
  useEffect(() => {
    const permission = getNotificationPermission();
    setPermissionStatus(permission.status);

    // Request permission if not yet requested
    if (permission.status === 'default') {
      requestNotificationPermission().then((result) => {
        setPermissionStatus(result.status);
      });
    }
  }, []);

  // Show desktop notifications for new unread notifications
  useEffect(() => {
    if (permissionStatus !== 'granted') return;

    // Find unread notifications that are recent (last 30 seconds)
    const recentUnread = notifications.filter((n) => {
      if (n.is_read) return false;
      const createdAt = new Date(n.created_at).getTime();
      const now = Date.now();
      return now - createdAt < 30000; // 30 seconds
    });

    // Show desktop notification for each recent unread notification
    recentUnread.forEach((notification) => {
      showNotification(notification.title, {
        body: notification.body,
        tag: notification.id, // Prevent duplicate notifications
        data: notification.data,
      });
    });
  }, [notifications, permissionStatus]);

  const handleClick = () => {
    // Just open the notification center - don't auto-mark as read
    // User can manually mark as read using the button in the modal
    openNotificationCenter();
  };

  return (
    <button
      onClick={handleClick}
      className="relative p-2 rounded-lg hover:bg-[#363232] transition-colors"
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
    >
      <Bell className="w-5 h-5 text-[#EBFFEE]" />
      {unreadCount > 0 && (
        <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-[#9B1421] rounded-full">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
}
